/**
 * CompetitionContent - Main Content Area Component
 *
 * Single responsibility: Rendering the main competition content.
 * Handles header, info, questions list, and footer display.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PickemDetailResponse } from '@low6-pickem/api-types';
import type { SelectionState } from '@/hooks/usePickemSelections';
import GameCardFullWidth from '@/components/game/GameCardFullWidth';
import Breadcrumb from '@/components/shared/Breadcrumb';
import { RevealOnScroll } from '@/components/shared/RevealOnScroll';
import PickProgressBar, {
  PICK_PROGRESS_BAR_HOST_TAB_OFFSET_PX,
  PICK_PROGRESS_BAR_INLINE_SPACER_PX,
  PICK_PROGRESS_BAR_SCROLL_PADDING_PX,
  PICK_PROGRESS_BAR_SCROLL_PADDING_WITH_HOST_TAB_PX,
  PICK_PROGRESS_BAR_STICKY_FOOTER_AD_OFFSET_PX,
} from './PickProgressBar';
import { withSafeAreaBottom } from './safeArea';
import { resolveEmbedBottomChromePx } from '@/utils/embedChrome';
import { QuestionRenderer } from './QuestionRenderer';
import StickyActionBar from './StickyActionBar';
import { sortQuestionsWithTiebreakerLast } from '@/utils/questions/questionSorting';
import { useTenant } from '@/contexts/TenantContext';
import AdSlot from '@/components/ads/AdSlot';
import { generateAdDivId } from '@/config/ads.config';
import { usePickemStickyFooterAd } from '@/hooks/usePickemStickyFooterAd';
import {
  getAnsweredQuestionsArray,
  countAnsweredQuestions,
} from '@/utils/questions/questionProgress';
import { getLeagueLogoUrl } from '@/utils/pickem-helpers';
import PickemLeaderboard from '@/components/leaderboard/PickemLeaderboard';
import { QuestionCardStyleProvider } from '@low6-pickem/ui';
import { COMPETITION_PICKS_SCROLL_ID } from './competitionScroll';

const SKY_PICKS_SUBMIT_REMINDER =
  'Make sure to submit your entry before arriving to your gate. Contest entries cannot be submitted once your flight lands.';

const isEmbeddedWindow = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
};

interface CompetitionContentProps {
  // Data
  pickemDetails: PickemDetailResponse | undefined;
  competitionData: any; // TODO: Proper typing from domain.ts
  timeDisplay: string;
  currentUserId: string | undefined;

  // Selections
  selections: SelectionState;
  selectionResults?: Record<string, boolean>;
  selectionPoints?: Record<string, number>;

  // Handlers
  onTeamSelect: (questionId: string, teamId: string | undefined) => void;
  onPropSelect: (questionId: string, answer: 'yes' | 'no') => void;
  onOverUnderSelect: (questionId: string, answer: 'over' | 'under' | undefined) => void;
  onTieBreakerChange: (value: string) => void;
  onQuickPick: () => void;
  onFavouritePick: () => void;
  onClear: () => void;
  onSave: () => void;

  // State
  isContestClosed?: boolean;
  isViewOnly: boolean;
  isResultsMode: boolean;
  isSaving: boolean;
  showLeaderBoardOnLoad: boolean;
  /**
   * True once the user has any submitted selections for this pickem.
   * Drives the My Picks / Leaderboard tab strip — pre-entry the tabs
   * are hidden so the user lands directly on the picks interface.
   */
  hasEntered: boolean;
  /**
   * Editing mode controlled by the parent so that flows like the
   * Picks Submitted modal closing can deterministically drop the user
   * back into the summary view (rather than relying on a state-
   * transition useEffect that may not fire in every scenario).
   */
  isEditing: boolean;
  setIsEditing: (next: boolean) => void;
  /**
   * Return-to-lobby handler. Only used when the tenant has Pick'em full
   * navigation enabled (pre-DKPLAT-347 behaviour), where a breadcrumb back
   * to the Lobby replaces the in-game tab strip.
   */
  onBack?: () => void;
  /**
   * When absent/false the Popular Pick button and its autofill flow are hidden,
   * and pick-percentage badges on option cards are not shown.
   * Driven by `pickem.style_config.showPopularPicks`.
   */
  showPopularPicks?: boolean;
  /**
   * When true, team/jersey artwork is served from the low-res `-low` blob
   * folders. Driven by `pickem.style_config.lowResImages` (DKPLAT-669).
   */
  lowResImages?: boolean;
  /**
   * Whether this contest may use licensed (IP) team artwork. Driven by
   * `pickem.league_ip` (DKPLAT-357). Absent is treated as false.
   */
  leagueIp?: boolean;
  /**
   * Contest picks-core team image folder from `style_config.teamAssetFolder`.
   * When set, remaps team marks into that folder on the frontend.
   */
  teamAssetFolder?: string | null;
  /**
   * When absent/false the T&C confirmation checkbox is not rendered in the
   * entry confirmation panel and the Submit button is only gated by isSubmitting.
   * Driven by `pickem.style_config.showTermsCheckbox`.
   */
  showTermsCheckbox?: boolean;
  /**
   * When true, renders the contest-details info icon on the game card (used when
   * the header banner is hidden — DKPLAT-437).
   */
  showInfoOnGameCard?: boolean;
  onInfoClick?: () => void;
  /**
   * When true, the standalone Quick Pick/Clear action row is dropped, because
   * those actions have moved into the three-dot overflow menu. Driven by
   * `pickem.style_config.header.showOverflowMenu` (DKPLAT-664). This stays true
   * even when the banner hosts the menu — the row goes either way.
   */
  showOverflowMenu?: boolean;
  /**
   * The overflow menu element to render in place of the game card's info icon,
   * built by CompetitionView. Undefined when the header banner is visible and
   * already hosting it, so the card never shows a second copy (DKPLAT-664).
   */
  overflowMenu?: React.ReactNode;
}

/**
 * Component that renders the main competition content area
 */
export const CompetitionContent: React.FC<CompetitionContentProps> = ({
  pickemDetails,
  competitionData,
  timeDisplay,
  selections,
  selectionResults,
  selectionPoints,
  currentUserId,
  onTeamSelect,
  onPropSelect,
  onOverUnderSelect,
  onTieBreakerChange,
  onQuickPick,
  onFavouritePick,
  onClear,
  onSave,
  isContestClosed = false,
  isViewOnly,
  isResultsMode,
  isSaving,
  showLeaderBoardOnLoad,
  hasEntered,
  isEditing,
  setIsEditing,
  onBack,
  showPopularPicks,
  lowResImages,
  leagueIp,
  teamAssetFolder,
  showTermsCheckbox,
  showInfoOnGameCard,
  onInfoClick,
  showOverflowMenu,
  overflowMenu,
}) => {
  const { t } = useTranslation();
  const {
    tenant,
    isAdsEnabled,
    adPlacements,
    isPlacementEnabled,
    pickemFullNavigation,
    pickemProgressBarMode,
  } = useTenant();
  const showAdPlaceholders = import.meta.env.MODE === 'development';
  const isSkyPicksTenant = tenant.code === 'sky';

  // Per-contest floating sticky-footer ad banner (DKPLAT-468). Toggled per
  // pickem from admin; gated by tenant ads being enabled inside the hook.
  const { adPlacement: stickyFooterAdPlacement, shouldShowAd: showStickyFooterAd } =
    usePickemStickyFooterAd(pickemDetails?.pickem.pickem_id);

  // Sort questions: regular questions first, tiebreaker last
  const sortedQuestions = sortQuestionsWithTiebreakerLast(pickemDetails?.pickem.questions || []);

  // Calculate answered questions for progress bar
  const answeredQuestions = getAnsweredQuestionsArray(sortedQuestions, selections);

  // Get indices of tiebreaker questions for progress bar star display
  const tiebreakerIndices = sortedQuestions
    .map((q, index) => (q.question_type?.toLowerCase() === 'tiebreaker' ? index : -1))
    .filter(index => index !== -1);

  // Check if any picks have been made
  const hasAnyPicks = countAnsweredQuestions(sortedQuestions, selections) > 0;

  const [showLeaderBoard, setShowLeaderBoard] = useState(showLeaderBoardOnLoad);
  const [showResults, setShowResults] = useState(!showLeaderBoard);

  // The picks-entry UI (questions + sticky bars + progress bar) only renders
  // when we're actually editing — first-time entry, or the user clicked
  // Edit Picks from the summary view. Edit state is parent-controlled.
  //
  // When the tenant has Pick'em full navigation enabled (pre-DKPLAT-347), the
  // picks UI is always shown (no summary-first gating) — the user lands
  // directly on editable picks regardless of entry state.
  const isEditingPicks = pickemFullNavigation ? true : !hasEntered || isEditing;

  const onResultsClick = () => {
    setShowResults(true);
    setShowLeaderBoard(false);
  };

  const onLeaderboardClick = () => {
    setShowResults(false);
    setShowLeaderBoard(true);
  };

  const gameCardStyleConfig = pickemDetails?.pickem?.style_config?.game_card as
    | Record<string, unknown>
    | undefined;
  const bgGradientStart = gameCardStyleConfig?.bg_gradient_start as string | undefined;
  const bgGradientEnd = gameCardStyleConfig?.bg_gradient_end as string | undefined;
  const borderColor = gameCardStyleConfig?.border_color as string | undefined;
  const questionNumberBg = gameCardStyleConfig?.question_number_bg as string | undefined;
  const questionNumberFg = gameCardStyleConfig?.question_number_fg as string | undefined;

  const ddStyleConfig = pickemDetails?.pickem?.style_config?.daily_double as
    | Record<string, unknown>
    | undefined;
  const ddCardGradientStart = ddStyleConfig?.card_gradient_start as string | undefined;
  const ddCardGradientEnd = ddStyleConfig?.card_gradient_end as string | undefined;
  const ddCardBorderColor = ddStyleConfig?.card_border_color as string | undefined;
  const ddNumberBadgeBg = ddStyleConfig?.number_badge_bg as string | undefined;
  const ddNumberBadgeFg = ddStyleConfig?.number_badge_fg as string | undefined;
  const ddButtonBg = ddStyleConfig?.button_bg as string | undefined;
  const ddButtonBorder = ddStyleConfig?.button_border as string | undefined;
  const ddBackgroundImage = ddStyleConfig?.background_image as string | undefined;
  const ddLogoImage = ddStyleConfig?.logo_image as string | undefined;
  const ddSecondaryImage = ddStyleConfig?.secondary_image as string | undefined;

  const contestCardStyleConfig = pickemDetails?.pickem?.style_config?.contest_card as
    | Record<string, unknown>
    | undefined;
  const contestCardBgGradientStart = contestCardStyleConfig?.bg_gradient_start as
    | string
    | undefined;
  const contestCardBgGradientEnd = contestCardStyleConfig?.bg_gradient_end as string | undefined;
  const contestCardBorderColor = contestCardStyleConfig?.border_color as string | undefined;

  const logosStyleConfig = pickemDetails?.pickem?.style_config?.logos as
    | Record<string, unknown>
    | undefined;
  const confirmationLogoUrl = logosStyleConfig?.confirmation_logo as string | undefined;

  // Optional submit reminder shown above the Submit Picks button (DKPLAT-448).
  // style_config is freeform JSON, so guard the type — a non-string value must
  // not reach PickProgressBar (it calls .trim()).
  const entryStyleConfig = pickemDetails?.pickem?.style_config?.entry as
    | Record<string, unknown>
    | undefined;
  const rawSubmitMessage = entryStyleConfig?.submit_message;
  const submitMessage =
    typeof rawSubmitMessage === 'string' && rawSubmitMessage.trim() !== ''
      ? rawSubmitMessage
      : isSkyPicksTenant
        ? SKY_PICKS_SUBMIT_REMINDER
        : undefined;

  // Optional entry-confirmation popup copy (DKPLAT-515). Supports markdown-style
  // links so tenants (e.g. SkyPicks) can link out to their official rules.
  // Falls back to the default copy inside PickProgressBar when blank.
  const rawConfirmationMessage = entryStyleConfig?.confirmation_message;
  const confirmationMessage =
    typeof rawConfirmationMessage === 'string' ? rawConfirmationMessage : undefined;

  const isPicksEditingScroll = showResults && isEditingPicks && !(isContestClosed && !hasEntered);
  const isPinnedProgressBar = pickemProgressBarMode === 'pinned';
  const isPinnedProgressBarEmbedded = isPinnedProgressBar && isEmbeddedWindow();

  // How far the DK native app's tab bar overlaps the bottom of this iframe, from
  // the UA (WebView tells) or DK's `?bottomInset` when they send one. 0 in a
  // browser. Read once per mount: neither input changes while the page is up.
  const embedBottomChromePx = useMemo(() => resolveEmbedBottomChromePx(), []);

  // Base offset lifts every bottom-pinned element — the floating sticky-footer
  // ad, the pinned submit CTA above it, the picks scroll padding — clear of the
  // host (DK native) tab bar. Two sources, the larger wins:
  //   - the pinned progress bar's fixed 72px when the tenant runs `pinned` mode
  //     inside an iframe (the SkyPicks arrangement);
  //   - the sniffed/declared native-app overlap, which is what the DK tenant
  //     needs. Before this the DK tenant resolved to 0 and the footer ad pinned
  //     flush to the WebView bottom, underneath the tab bar (DKPLAT-744).
  const pinnedHostTabOffsetPx = isPinnedProgressBarEmbedded
    ? PICK_PROGRESS_BAR_HOST_TAB_OFFSET_PX
    : 0;
  const pickProgressHostTabOffsetPx = Math.max(pinnedHostTabOffsetPx, embedBottomChromePx);
  // The pinned-mode scroll-padding constant already bakes its own 72px in; only
  // the part of the lift that comes from the native chrome on top of that is
  // extra padding the last question needs.
  const embedChromeScrollPaddingPx = pickProgressHostTabOffsetPx - pinnedHostTabOffsetPx;
  // Inline layout: how much the measured chrome exceeds the bar's own legacy spacer.
  const inlineHostChromeTopUpPx = Math.max(
    0,
    embedBottomChromePx - PICK_PROGRESS_BAR_INLINE_SPACER_PX
  );

  // The in-play sticky footer banner renders only while the user is actively
  // picking — the same gate as the pinned submit CTA rendered below.
  const isStickyFooterAdVisible =
    isPicksEditingScroll &&
    !isViewOnly &&
    !isResultsMode &&
    !!showStickyFooterAd &&
    !!stickyFooterAdPlacement;
  // GAM can answer "no ad" for the footer slot. When it does, the banner
  // collapses to nothing, so nothing above it should keep reserving the
  // banner's height — otherwise the submit CTA floats 106px above empty space.
  const [isStickyFooterAdEmpty, setIsStickyFooterAdEmpty] = useState(false);
  const stickyFooterAdDivId = `pickem-sticky-footer-${pickemDetails?.pickem.pickem_id ?? ''}`;
  // Keep this parent state in lockstep with AdSlot's own reset-on-divId-change:
  // the slot goes back to 'pending' whenever its divId changes, so a stale
  // "empty" here would otherwise keep the bar hidden for a slot that may fill.
  useEffect(() => {
    setIsStickyFooterAdEmpty(false);
  }, [stickyFooterAdDivId]);
  const isStickyFooterAdOccupying = isStickyFooterAdVisible && !isStickyFooterAdEmpty;
  const stickyFooterAdOffsetPx = isStickyFooterAdOccupying
    ? PICK_PROGRESS_BAR_STICKY_FOOTER_AD_OFFSET_PX
    : 0;

  // The banner pins just above the host tab bar; the submit CTA lifts above the
  // banner so the CTA is never covered, and the scroll padding is extended so
  // the last question is never hidden behind the banner.
  const stickyFooterAdBottomOffsetPx = pickProgressHostTabOffsetPx;
  const pickProgressBottomOffsetPx = pickProgressHostTabOffsetPx + stickyFooterAdOffsetPx;
  // Two layouts for the footer stack:
  //
  //   pinned (SkyPicks): CTA and banner both hover over the questions, so the
  //   picks list reserves room for both underneath its last question.
  //
  //   inline (DraftKings): the banner is `position: sticky` IN the flow, placed
  //   just before the inline progress bar. It clings to the viewport bottom
  //   (above the host chrome) while the questions scroll past, and the moment
  //   its natural slot — directly above the progress bar — scrolls into view,
  //   the bar carries it up with it. Nothing hovers over the last question, so
  //   the list reserves nothing; only the host chrome needs clearing at the
  //   very end, which the spacer after the bar does (DKPLAT-744).
  const picksScrollPaddingBottomPx = isPinnedProgressBar
    ? (isPinnedProgressBarEmbedded
        ? PICK_PROGRESS_BAR_SCROLL_PADDING_WITH_HOST_TAB_PX
        : PICK_PROGRESS_BAR_SCROLL_PADDING_PX) +
      stickyFooterAdOffsetPx +
      embedChromeScrollPaddingPx
    : 0;

  // The banner is positioned with `env(safe-area-inset-bottom)` added to its
  // offset, so everything stacked above it — the pinned CTA and the reserved
  // scroll padding — has to include the same inset, otherwise the clearance is
  // short by exactly the inset on devices where it is non-zero (DKPLAT-694).
  const picksScrollPaddingBottom =
    isPinnedProgressBar && isStickyFooterAdOccupying
      ? withSafeAreaBottom(picksScrollPaddingBottomPx)
      : `${picksScrollPaddingBottomPx}px`;

  const gameCard = (
    <div className={isPicksEditingScroll ? 'mt-3' : 'mt-[22px]'}>
      <GameCardFullWidth
        id={pickemDetails?.pickem.pickem_id || ''}
        title={competitionData.title}
        prize={pickemDetails?.pickem.prize_text_en || ''}
        state={competitionData.state}
        gameDate={new Date(competitionData.gameDate)}
        leagueLogoUrl={getLeagueLogoUrl(pickemDetails?.pickem.sport as string)}
        totalEntries={competitionData.totalEntries}
        competitionData={competitionData}
        showLeaderBoard={showLeaderBoard}
        showResults={showResults}
        onViewLeaderboard={onLeaderboardClick}
        onViewResults={onResultsClick}
        bgGradientStart={contestCardBgGradientStart}
        bgGradientEnd={contestCardBgGradientEnd}
        borderColor={contestCardBorderColor}
        showInfoButton={showInfoOnGameCard}
        onInfoClick={onInfoClick}
        headerAction={overflowMenu}
      />
    </div>
  );

  const breadcrumbNav = pickemFullNavigation && (
    <div className="px-4 pt-4 pb-2">
      <Breadcrumb
        items={[
          ...(onBack ? [{ label: 'Lobby', onClick: onBack }] : []),
          { label: competitionData.title },
        ]}
      />
    </div>
  );

  const picksTabStrip = !pickemFullNavigation && hasEntered && (
    <div className="px-4 pt-1 pb-1">
      <div className="flex items-center border-b border-border/20">
        <button
          type="button"
          data-test-id="game-tab-my-picks"
          onClick={() => {
            setShowResults(true);
            setShowLeaderBoard(false);
          }}
          className={`flex-1 py-2 px-4 text-sm font-gilroy font-semibold transition-smooth relative whitespace-nowrap ${
            showResults ? 'text-white' : 'text-text-secondary hover:text-text-primary'
          }`}
        >
          {t('tabs.myPicks')}
          {showResults && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-pickem-primary" />
          )}
        </button>
        <button
          type="button"
          data-test-id="game-tab-leaderboard"
          onClick={() => {
            setShowResults(false);
            setShowLeaderBoard(true);
          }}
          className={`flex-1 py-2 px-4 text-sm font-gilroy font-semibold transition-smooth relative whitespace-nowrap ${
            showLeaderBoard ? 'text-white' : 'text-text-secondary hover:text-text-primary'
          }`}
        >
          {t('tabs.leaderboard')}
          {showLeaderBoard && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-pickem-primary" />
          )}
        </button>
      </div>
    </div>
  );

  return (
    <QuestionCardStyleProvider
      value={{
        bgGradientStart,
        bgGradientEnd,
        borderColor,
        questionNumberBg,
        questionNumberFg,
        ddCardGradientStart,
        ddCardGradientEnd,
        ddCardBorderColor,
        ddNumberBadgeBg,
        ddNumberBadgeFg,
        ddButtonBg,
        ddButtonBorder,
        ddBackgroundImage,
        ddLogoImage,
        ddSecondaryImage,
      }}
    >
      <div className="relative flex flex-col">
        {/* Breadcrumb / tabs / game card stay fixed unless the user is actively
            picking — then they scroll with the questions so the tile isn't pinned. */}
        {!isPicksEditingScroll && breadcrumbNav}
        {!isPicksEditingScroll && picksTabStrip}
        {!isPicksEditingScroll && gameCard}

        {showLeaderBoard && (
          <div className="p-0">
            <PickemLeaderboard
              pickemId={pickemDetails?.pickem.pickem_id}
              isOpen={showLeaderBoard}
              onClose={() => {}}
              currentUserId={currentUserId}
              contestStartUtc={pickemDetails?.pickem.start_utc}
            />
          </div>
        )}

        {/* Summary view — shown on My Picks tab once the user has entered, but
          hasn't pressed Edit Picks. The contest card above already covers the
          summary information; this section just gives them a way back into
          editing. Skipped under full navigation, where picks are always
          directly editable. */}
        {isContestClosed && !hasEntered && showResults && (
          <div
            className="mx-4 mt-6 p-4 rounded-xl bg-app-surface border border-border/30 text-center"
            data-test-id="contest-closed-message"
          >
            <p className="text-text-primary font-gilroy text-sm leading-relaxed">
              {t('game.contestClosed')}
            </p>
          </div>
        )}

        {!pickemFullNavigation && showResults && hasEntered && !isEditing && (
          <div className="flex justify-center mt-4 px-4">
            <button
              type="button"
              data-test-id="edit-picks-button"
              onClick={() => setIsEditing(true)}
              className="px-6 py-3 rounded-full bg-app-surface border border-border/30 text-text-primary font-gilroy font-semibold hover:bg-app-surface-hover transition-smooth disabled:opacity-50"
            >
              {isViewOnly ? t('game.actions.viewPicks') : t('game.actions.editPicks')}
            </button>
          </div>
        )}

        {isPicksEditingScroll && (
          <>
            <div
              id={COMPETITION_PICKS_SCROLL_ID}
              style={{ paddingBottom: picksScrollPaddingBottom }}
            >
              {breadcrumbNav}
              {picksTabStrip}
              {gameCard}

              {/* Show Quick/Fav pick buttons. Suppressed when the overflow menu
                  is enabled — those actions live in the menu instead, freeing
                  the row for questions (DKPLAT-664). */}
              {!isViewOnly && !isResultsMode && !showOverflowMenu && (
                <StickyActionBar
                  onQuickPick={onQuickPick}
                  onFavouritePick={onFavouritePick}
                  onClear={onClear}
                  hasAnyPicks={hasAnyPicks}
                  showFavouritePick={showPopularPicks === true}
                />
              )}

              {/* Questions List — space-y-4 (not -3) so the 24px question-number
                  badge, which is centred on the card's top edge and so protrudes
                  12px upward, keeps clearance from the card above. */}
              <div className="px-4 pb-3 pt-2 space-y-4">
                {(() => {
                  const nodes: React.ReactNode[] = [];
                  let nonTiebreakerCount = 0;
                  // Counts only the cards that actually render, so the reveal cascade is
                  // not thrown off by the ad slots pushed into `nodes` between them.
                  let renderedCards = 0;

                  for (const question of sortedQuestions) {
                    const isTiebreaker = question.question_type?.toLowerCase() === 'tiebreaker';
                    const questionNumber = isTiebreaker ? undefined : nonTiebreakerCount + 1;

                    nodes.push(
                      // Revealed on scroll, staggered across the opening screenful. One
                      // wrapper per question card, so `space-y-4` on the container still
                      // separates them exactly as before.
                      <RevealOnScroll key={question.question_id} index={renderedCards}>
                        <QuestionRenderer
                          question={question}
                          questionNumber={questionNumber}
                          selectedTeams={selections.teams}
                          selectedProps={selections.props}
                          selectedOverUnder={selections.overUnder}
                          tieBreaker={selections.tieBreaker}
                          onTeamSelect={onTeamSelect}
                          onPropSelect={onPropSelect}
                          onOverUnderSelect={onOverUnderSelect}
                          onTieBreakerChange={onTieBreakerChange}
                          isViewOnly={isViewOnly}
                          selectionResults={isResultsMode ? selectionResults : undefined}
                          selectionPoints={isResultsMode ? selectionPoints : undefined}
                          showPopularPicks={showPopularPicks}
                          lowResImages={lowResImages}
                          leagueIp={leagueIp}
                          teamAssetFolder={teamAssetFolder}
                        />
                      </RevealOnScroll>
                    );
                    renderedCards += 1;

                    if (!isTiebreaker) {
                      nonTiebreakerCount += 1;

                      // Insert ads after pick 2 and pick 4, controlled per-tenant via placements config
                      const shouldShowAfter2 =
                        nonTiebreakerCount === 2 &&
                        (isAdsEnabled || showAdPlaceholders) &&
                        adPlacements &&
                        isPlacementEnabled('PICKS_AFTER_2');
                      const shouldShowAfter4 =
                        nonTiebreakerCount === 4 &&
                        (isAdsEnabled || showAdPlaceholders) &&
                        adPlacements &&
                        isPlacementEnabled('PICKS_AFTER_4');

                      if (shouldShowAfter2 || shouldShowAfter4) {
                        const placementKey = shouldShowAfter2 ? 'PICKS_AFTER_2' : 'PICKS_AFTER_4';
                        const index = shouldShowAfter2 ? 0 : 1;
                        nodes.push(
                          // The vertical spacing lives on the AdSlot container itself,
                          // not on a wrapper: an unfilled slot renders `display: none`,
                          // which takes its own margins with it. On a wrapper the
                          // margins would survive and leave a visible gap between
                          // questions where the ad should have collapsed.
                          <AdSlot
                            key={`ad-after-${nonTiebreakerCount}`}
                            divId={generateAdDivId(adPlacements, placementKey, true, index)}
                            adPlacement={adPlacements[placementKey]}
                            placement={`picks-after-${nonTiebreakerCount}`}
                            className="my-2 rounded-lg overflow-hidden"
                          />
                        );
                      }
                    }
                  }

                  return nodes;
                })()}
              </div>
            </div>

            {/* Per-contest floating sticky-footer ad banner (DKPLAT-468).
                Sits just above the host tab bar, at a lower z-index than the
                submit CTA (z-40) so the CTA is never covered. Persists as the
                user scrolls and answers questions. The bar is full-bleed and
                carries the same `bg-app-background` as the CTA, so the two read
                as one piece of app chrome and questions no longer scroll
                through the gutters either side of the creative (DKPLAT-694).

                Pinned layout: `fixed`, the CTA floats above it.
                Inline layout: `sticky`, in the flow immediately before the
                inline progress bar — see the padding notes above. When the bar
                scrolls into view it pushes the banner up ahead of it
                (DKPLAT-744). */}
            {isStickyFooterAdVisible && stickyFooterAdPlacement && (
              <div
                className={`${isPinnedProgressBar ? 'fixed inset-x-0' : 'sticky w-full'} z-30 flex justify-center bg-app-background px-3 py-2 sm:px-4 ${
                  isStickyFooterAdEmpty ? 'hidden' : ''
                }`}
                style={{ bottom: withSafeAreaBottom(stickyFooterAdBottomOffsetPx) }}
                data-test-id="pickem-sticky-footer-ad"
                data-test-layout={isPinnedProgressBar ? 'fixed' : 'sticky'}
              >
                <AdSlot
                  divId={stickyFooterAdDivId}
                  adPlacement={stickyFooterAdPlacement}
                  placement="pickem-sticky-footer"
                  className="max-w-5xl w-full rounded-lg overflow-hidden"
                  // The slot stays mounted when unfilled (unmounting would destroy
                  // it and lose any later refresh); the bar around it is hidden
                  // instead, so its padding and background go with it.
                  onRenderEnded={isEmpty => setIsStickyFooterAdEmpty(isEmpty)}
                />
              </div>
            )}

            {/* Pick Progress Bar. Pinned: a sticky footer that floats above the
              banner. Inline: the last thing in the flow, so it carries the sticky
              banner above it up into view. Only when actually picking (not on the
              leaderboard tab, not in summary mode, not view-only, not results). */}
            {!isViewOnly && !isResultsMode && (
              <PickProgressBar
                answeredQuestions={answeredQuestions}
                tiebreakerIndices={tiebreakerIndices}
                onSubmit={onSave}
                className="max-w-5xl w-full mx-auto lg:max-w-[clamp(1024px,90vw,1600px)]"
                layoutMode={pickemProgressBarMode}
                bottomOffsetPx={pickProgressBottomOffsetPx}
                hasStickyFooterBelow={isStickyFooterAdOccupying}
                isSubmitting={isSaving}
                confirmationLogoUrl={confirmationLogoUrl}
                fullNavigation={pickemFullNavigation}
                showTermsCheckbox={showTermsCheckbox}
                submitMessage={submitMessage}
                confirmationMessage={confirmationMessage}
              />
            )}

            {/* Inline layout only: the document ends under the host tab bar, so
                clear it once, after the last in-flow element. The inline bar
                already carries a legacy 112px spacer for this; only the part of
                the measured chrome that exceeds it is added here, so the total
                clearance is max(legacy, measured) rather than their sum. The
                pinned layout clears it through the picks scroll padding instead. */}
            {!isPinnedProgressBar && inlineHostChromeTopUpPx > 0 && (
              <div
                aria-hidden="true"
                style={{ height: withSafeAreaBottom(inlineHostChromeTopUpPx) }}
                data-test-id="host-chrome-spacer"
              />
            )}
          </>
        )}
      </div>
    </QuestionCardStyleProvider>
  );
};
